import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "../../utils/toast.js";
import { getMyBookings, cancelBooking } from "../../services/bookingService.js";
import ConfirmDialog from "../../panel/components/ConfirmDialog.jsx";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_BADGE = {
  Upcoming: "badge-warn",
  Completed: "badge-live",
  Cancelled: "badge-danger",
};

export default function MyBooking() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingToCancel, setBookingToCancel] = useState(null);

  useEffect(() => {
    fetchBookings(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function fetchBookings(activeFilter) {
    setLoading(true);
    try {
      const res = await getMyBookings(activeFilter);
      if (res.success) {
        setBookings(res.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't load your bookings.");
    } finally {
      setLoading(false);
    }
  }

  async function confirmCancelBooking() {
    const booking = bookingToCancel;
    setBookingToCancel(null);
    try {
      const res = await cancelBooking(booking._id);
      if (!res.success) {
        toast.error(res.message || "Couldn't cancel the booking.");
        return;
      }
      toast.success(res.message || "Booking cancelled.");
      fetchBookings(filter);
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't cancel the booking.");
    }
  }

  return (
    <>
      <section className="dash-section active" data-section="bookings">
        <div className="section-head-row">
          <h2>My Bookings</h2>
        </div>
        <div className="booking-filter-tabs">
          {FILTERS.map((f) => (
            <button
              type="button"
              key={f.key}
              className={`filter-tab ${filter === f.key ? "active" : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="wizard-hint">Loading your bookings...</p>
        ) : bookings.length > 0 ? (
          <div className="bookings-list">
            {bookings.map((booking) => (
              <article className="booking-card" key={booking._id}>
                <div className="booking-card-top">
                  <div>
                    <span className="booking-card-route">
                      {booking.fromCity} → {booking.toCity}
                    </span>
                    <span className="booking-card-order">Order # {booking.orderNumber}</span>
                  </div>
                  <span
                    className={`booking-card-status ${STATUS_BADGE[booking.displayStatus] || "badge-muted"}`}
                  >
                    {booking.displayStatus}
                  </span>
                </div>
                <div className="booking-card-meta">
                  <span className="meta-mode">
                    🧑‍🤝‍🧑{" "}
                    {typeof booking.seatsCount === "number"
                      ? `${booking.seatsCount} seat(s)`
                      : booking.seatsCount}
                  </span>
                  <span className="meta-date">📅 {booking.travelDate}</span>
                </div>
                <div className="booking-card-bottom">
                  <span className="booking-card-price">₹{booking.totalFare}</span>
                  <div className="booking-card-actions">
                    {booking.displayStatus === "Upcoming" && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm booking-cancel-btn"
                        onClick={() => setBookingToCancel(booking)}
                      >
                        Cancel
                      </button>
                    )}
                    {/* <button
                      type="button"
                      className="btn btn-outline btn-sm booking-invoice-btn"
                      onClick={() => toast.info("Invoice download coming soon.")}
                    >
                      Invoice
                    </button> */}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-state show">
            <h3>No bookings here</h3>
            <p>Nothing matches this filter yet.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate("/panel_customer/bookride")}
            >
              🔍 Search cabs
            </button>
          </div>
        )}
      </section>

      <ConfirmDialog
        isOpen={!!bookingToCancel}
        title="Cancel this booking?"
        message={
          bookingToCancel &&
          `Cancel your ${bookingToCancel.fromCity} → ${bookingToCancel.toCity} trip on ${bookingToCancel.travelDate}? This can't be undone.`
        }
        confirmLabel="Yes, cancel"
        cancelLabel="No, keep it"
        onConfirm={confirmCancelBooking}
        onCancel={() => setBookingToCancel(null)}
      />
    </>
  );
}
